import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { from, of, Observable, combineLatest } from 'rxjs';
import { AngularFirestore } from '@angular/fire/firestore';
import { AngularFireAuth } from '@angular/fire/auth';

import * as AccountActions from './account.actions';
import { Router } from '@angular/router';
import * as firebase from 'firebase';
import { Provider } from 'src/app/core/enums/provider.enum';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Update } from '@ngrx/entity';
import { User } from 'src/app/core/models/user.model';



@Injectable()
export class AccountEffects {

  loadAccount$ = createEffect(() => {
    return this.afa.user.pipe(
      switchMap(user => user ? this.afs.doc(`users/${user.uid}`).valueChanges() : of(null)),
      map(user => user?.uid ? AccountActions.authenticated({ user }) : AccountActions.notAuthenticated()));
  });

  login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountActions.login),
      switchMap(action => this.sighInWith(action.provider, action.credentials).pipe(
        map(() => AccountActions.loginSuccess()),
        tap(() => this.router.navigateByUrl('/')),
        catchError(error => {
          this.snackBar.open(error.message, 'Close');
          return of(AccountActions.loginFailure({ error }));
        })
      ))
    );
  });

  signUp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountActions.signUp),
      switchMap(action => this.sighUpWith(action.provider, action.credentials).pipe(
        map(() => AccountActions.signUpSuccess()),
        tap(() => this.router.navigateByUrl('/')),
        catchError(error => {
          this.snackBar.open(error.message, 'Close');
          return of(AccountActions.signUpFailure({ error }));
        })
      ))
    );
  });

  logout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountActions.logout),
      switchMap(() => this.afa.signOut()),
      tap(() => this.router.navigateByUrl('/login'))
    );
  }, { dispatch: false });



  constructor(
    private actions$: Actions,
    private afs: AngularFirestore,
    private afa: AngularFireAuth,
    private router: Router,
    private snackBar: MatSnackBar
  ) {}

  private sighInWith(provider: string, credentials?: any): Observable<firebase.auth.UserCredential | null> {
    switch (provider) {
      case Provider.GOOGLE:
        return this.signInWithGoogle();
      case Provider.EMAIL_AND_PASSWORD:
        return from(this.afa.signInWithEmailAndPassword(
          credentials.email,
          credentials.password
        ));
      default:
        return of(null);
    }
  }

  private sighUpWith(provider: string, credentials?: any): Observable<firebase.auth.UserCredential | null> {
    switch (provider) {
      case Provider.GOOGLE:
        return this.signInWithGoogle().pipe(
          switchMap(userCredential => {
            return this.updateStoreUser({
              id: userCredential.user.uid,
              changes: {
                langKey: credentials.langKey
              }
            }).pipe(
              map(() => userCredential)
            );
          })
        );
      case Provider.EMAIL_AND_PASSWORD:
        return from(this.afa.createUserWithEmailAndPassword(
          credentials.email,
          credentials.password
        )).pipe(
          switchMap(userCredential => {
            return combineLatest([
              this.updateAuthProfile(credentials.displayName),
              this.updateStoreUser({
                id: userCredential.user.uid,
                changes: {
                  displayName: credentials.displayName,
                  langKey: credentials.langKey,
                  ...userCredential.user.photoURL ? { photoURL: userCredential.user.photoURL } : {}
                }
              })
            ]).pipe(
              map(() => userCredential)
            );
          })
        );
      default:
        return of(null);
    }
  }

  private signInWithGoogle(): Observable<firebase.auth.UserCredential> {
    return from(this.afa.signInWithPopup(new firebase.auth.GoogleAuthProvider()));
  }

  private updateStoreUser(update: Update<User>): Observable<void> {
    return from(this.afs.collection('users').doc(update.id as string).set(update.changes, { merge: true }));
  }

  public updateAuthProfile(displayName: string, photoURL?: string): Observable<void> {
    return from(this.afa.currentUser.then((user: firebase.User) => user.updateProfile({ displayName, ...photoURL ? { photoURL } : {} })));
  }

}
